/**
 * Merci : la navigation par ancres.
 *
 * Le même composant sert deux pages qui n'ont ni la même source ni le même
 * markup serveur :
 *
 *   - la **page FAQ**, dont les liens sont rendus par Liquid depuis les
 *     metaobjets `faq_categories`. Ici le script ne fait que marquer l'ancre
 *     courante au défilement.
 *   - le **gabarit de page par défaut**, dont les liens n'existent pas côté
 *     serveur : ils sont bâtis ici, à partir des `h2` du contenu de la page.
 *
 * `data-headings` distingue les deux : quand il porte un sélecteur, le script
 * balaie les `h2` qu'il contient et construit la liste. Sans lui, il prend les
 * liens déjà présents.
 *
 * --- Pourquoi les `h2` sont lus en JavaScript et non en Liquid
 *
 * Parce que le contenu d'une page est **un seul champ de texte riche**. Pour
 * l'ancrer, il faut poser un `id` sur chaque titre, et Liquid ne sait le faire
 * qu'en découpant le HTML à la chaîne — fragile face au balisage de l'éditeur,
 * et cassé dès qu'un attribut change. Le DOM, lui, donne les titres comme
 * objets et accepte qu'on les nomme.
 *
 * --- Ce qu'on perd sans JavaScript, et ce qu'on ne perd pas
 *
 * Sur la page par défaut, la navigation ne s'affiche pas du tout : elle part
 * `hidden` et n'est révélée qu'une fois bâtie. Le contenu, lui, est entier et
 * lisible — c'est du texte riche rendu par le serveur. On perd un raccourci,
 * pas de l'information.
 *
 * Sur la FAQ, les liens sont rendus par le serveur : seul le repérage au
 * défilement disparaît.
 *
 * --- L'ancre courante
 *
 * Elle demande de savoir où l'on est dans la page, ce qu'aucune règle CSS ne
 * dit : `:target` ne répond qu'au clic, jamais au défilement. D'où
 * l'`IntersectionObserver`, dont le `rootMargin` ramène la ligne de
 * déclenchement sous l'en-tête — sans lui, une rubrique serait dite courante
 * alors qu'elle est encore cachée derrière.
 */

/** Ramène un titre à un identifiant utilisable dans une URL. */
function versIdentifiant(texte) {
  return (
    texte
      .normalize('NFD')
      // Les diacritiques, que `NFD` vient de détacher de leur lettre.
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  );
}

class MerciAnchorNav extends HTMLElement {
  #observer;

  connectedCallback() {
    this.liste = this.querySelector('.merci-anchor-nav__list');
    if (!this.liste) return;

    const selecteur = this.dataset.headings;
    const cibles = selecteur ? this.#batir(selecteur) : this.#reprendre();

    if (cibles.length === 0) {
      // Rien à pointer. Sur la page par défaut la navigation reste donc
      // masquée, et la grille lui rend sa colonne — voir le `:has()` de
      // `styles/_page.scss`.
      return;
    }

    this.hidden = false;
    this.#surveiller(cibles);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /**
   * Bâtit la liste depuis les `h2` du conteneur désigné, et rend les titres
   * ainsi nommés.
   *
   * @param {string} selecteur
   * @returns {HTMLElement[]}
   */
  #batir(selecteur) {
    const source = document.querySelector(selecteur);
    if (!source) return [];

    const titres = [...source.querySelectorAll('h2')].filter((titre) => titre.textContent.trim() !== '');
    if (titres.length === 0) return [];

    const fragment = document.createDocumentFragment();
    const pris = new Set();

    for (const [rang, titre] of titres.entries()) {
      // Un `id` déjà posé par l'éditeur est respecté : c'est peut-être la cible
      // d'un lien écrit à la main ailleurs sur le site.
      if (!titre.id) {
        const racine = versIdentifiant(titre.textContent.trim()) || 'section';
        let identifiant = racine;

        // Deux titres identiques dans une même page ne sont pas une faute de
        // saisie improbable : « Article 1 », « Article 1 »… Sans ce compteur,
        // les deux liens mèneraient au premier.
        let suffixe = 2;
        while (pris.has(identifiant) || document.getElementById(identifiant)) {
          identifiant = `${racine}-${suffixe}`;
          suffixe += 1;
        }

        titre.id = identifiant;
      }

      pris.add(titre.id);

      const element = document.createElement('li');
      const lien = document.createElement('a');

      lien.className = 'merci-anchor-nav__link';
      lien.href = `#${titre.id}`;
      lien.textContent = titre.textContent.trim();
      if (rang === 0) lien.setAttribute('aria-current', 'true');

      element.append(lien);
      fragment.append(element);
    }

    this.liste.append(fragment);
    return titres;
  }

  /**
   * Reprend les liens déjà rendus par le serveur et retrouve leurs cibles.
   *
   * @returns {HTMLElement[]}
   */
  #reprendre() {
    const cibles = [];

    for (const lien of this.liste.querySelectorAll('.merci-anchor-nav__link')) {
      const identifiant = lien.getAttribute('href')?.slice(1);
      const cible = identifiant ? document.getElementById(identifiant) : null;
      if (cible) cibles.push(cible);
    }

    return cibles;
  }

  /** @param {HTMLElement[]} cibles */
  #surveiller(cibles) {
    const liens = new Map();

    for (const lien of this.liste.querySelectorAll('.merci-anchor-nav__link')) {
      const identifiant = lien.getAttribute('href')?.slice(1);
      if (identifiant) liens.set(identifiant, lien);
    }

    this.#observer = new IntersectionObserver(
      (entrees) => {
        const visibles = entrees
          .filter((entree) => entree.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visibles.length > 0) this.#marquer(liens, visibles[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px' }
    );

    for (const cible of cibles) this.#observer.observe(cible);

    // Au chargement, la première ancre est courante — c'est ce que montre la
    // maquette, et l'observateur n'a encore rien annoncé.
    this.#marquer(liens, cibles[0].id);
  }

  /**
   * @param {Map<string, HTMLElement>} liens
   * @param {string} identifiant
   */
  #marquer(liens, identifiant) {
    for (const [cle, lien] of liens) {
      // `aria-current` porte l'information pour les lecteurs d'écran et sert de
      // crochet au CSS : pas de classe en double.
      if (cle === identifiant) lien.setAttribute('aria-current', 'true');
      else lien.removeAttribute('aria-current');
    }
  }
}

if (!customElements.get('merci-anchor-nav')) {
  customElements.define('merci-anchor-nav', MerciAnchorNav);
}
